import React, { memo, useCallback } from 'react';
import type { CSSProperties } from 'react';
import type { HyperlinkLabel as HyperlinkLabelType, LocalizedLabel } from '@segmently/schema-models/v2';
import { resolveLabel, type TemplateVariables } from '../../../utils/contentResolver';
import { mapTypographyStyles, mapFrameLayoutStyles } from '../../../utils/styleMapper';
import { parseTemplateTags, type ParsedSegment } from '../../../utils/templateTagParser';
import { useFontOverrides } from '@modules/font-config/context/FontConfigContext';
import { InlineCountdown } from '../InlineCountdown';
import styles from './HyperlinkLabel.module.css';

export interface HyperlinkLabelEventContext {
  uri: string;
  linkType: string;
}

export interface HyperlinkLabelProps {
  hyperlinkLabel: HyperlinkLabelType | undefined;
  locale: string;
  variables?: TemplateVariables;
  linkType?: string;
  onClick?: (context: HyperlinkLabelEventContext) => void;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
}

export const HyperlinkLabel = memo(function HyperlinkLabel({
  hyperlinkLabel,
  locale,
  variables,
  linkType = 'link',
  onClick,
  className,
  'data-testid': testId,
  editPath,
}: HyperlinkLabelProps): React.ReactElement | null {
  const fontOverrides = useFontOverrides();

  // ⚠️ Rules of Hooks: all hooks run before the conditional early-returns —
  // a label can materialize mid-session (theme/editor writes) and a pre-hook
  // return flips the hook count ("Rendered more hooks…" crash).
  const handleClick = useCallback(() => {
    if (onClick) {
      onClick({ uri: hyperlinkLabel?.uri ?? '', linkType });
    }
  }, [onClick, hyperlinkLabel?.uri, linkType]);

  if (!hyperlinkLabel) {
    return null;
  }

  // Some Paywall editor data stores legal link text under `.content` so it can use
  // the same V2 template label editor as other labels while keeping `uri` on the link.
  const renderableLabel = ((hyperlinkLabel as HyperlinkLabelType & { content?: LocalizedLabel }).content ??
    hyperlinkLabel) as unknown as LocalizedLabel;

  // HyperlinkLabel has same translations shape as LocalizedLabel.
  const displayText = resolveLabel(renderableLabel, locale, variables);

  if (!displayText) {
    return null;
  }

  const allTypographyStyles = mapTypographyStyles(renderableLabel.appearance, fontOverrides);
  const { backgroundColor, borderRadius, ...textStyles } = allTypographyStyles;

  const containerStyle: CSSProperties = {
    ...textStyles,
    ...mapFrameLayoutStyles(renderableLabel.layout),
  };

  const bgSpanStyles: CSSProperties | undefined = backgroundColor
    ? {
        backgroundColor,
        ...(borderRadius !== undefined && { borderRadius }),
        padding: '0 4px',
        boxDecorationBreak: 'clone',
        WebkitBoxDecorationBreak: 'clone',
      } as CSSProperties
    : undefined;

  const labels = renderableLabel.templateParams?.labels || {};
  const links = renderableLabel.templateParams?.links || {};
  const countdowns = renderableLabel.templateParams?.countdowns || {};
  const parseResult = parseTemplateTags(displayText, labels, links, {}, countdowns);

  const renderSegments = (segments: ParsedSegment[]) => segments.map((segment, index) => {
    if (segment.type === 'text') {
      return <span key={index}>{segment.content}</span>;
    }

    if (segment.type === 'label') {
      return (
        <span key={index} style={mapTypographyStyles(segment.styles, fontOverrides)}>
          {segment.content}
        </span>
      );
    }

    if (segment.type === 'countdown') {
      const countdownEl = (
        <InlineCountdown
          key={segment.labelStyles ? undefined : index}
          config={segment.config}
          data-testid={`inline-countdown-${segment.timerName}`}
        />
      );
      if (segment.labelStyles) {
        return <span key={index} style={mapTypographyStyles(segment.labelStyles, fontOverrides)}>{countdownEl}</span>;
      }
      return countdownEl;
    }

    if (segment.type === 'link') {
      const labelSx = segment.labelStyles ? mapTypographyStyles(segment.labelStyles, fontOverrides) : undefined;
      const displayValue = segment.href || `@${segment.linkName}`;
      return <span key={index} style={labelSx}>{displayValue}</span>;
    }

    return null;
  });

  const hasTemplateTags = parseResult.hasLabels || parseResult.hasLinks || parseResult.hasCountdowns;
  const content = bgSpanStyles ? (
    <span style={bgSpanStyles}>
      {hasTemplateTags ? renderSegments(parseResult.segments) : displayText}
    </span>
  ) : (
    hasTemplateTags ? renderSegments(parseResult.segments) : <span>{displayText}</span>
  );

  const containerClassName = [styles.hyperlinkLabel, className].filter(Boolean).join(' ');
  const hasUri = hyperlinkLabel.uri && hyperlinkLabel.uri.trim() !== '';

  if (hasUri) {
    return (
      <a
        href={hyperlinkLabel.uri}
        target="_blank"
        rel="noopener noreferrer"
        className={containerClassName}
        style={containerStyle}
        onClick={handleClick}
        data-testid={testId}
        data-edit-path={editPath}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={containerClassName}
      style={containerStyle}
      onClick={handleClick}
      data-testid={testId}
      data-edit-path={editPath}
    >
      {content}
    </button>
  );
});

HyperlinkLabel.displayName = 'HyperlinkLabel';
